import React from 'react';
import { X, FileText, FileSpreadsheet, File } from 'lucide-react';
import type { PendingFile } from '@/hooks/useFileUpload';

interface FilePreviewChipsProps {
  files: PendingFile[];
  onRemove: (id: string) => void;
  disabled?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string): React.ReactNode {
  if (mimeType === 'text/csv' || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return <FileSpreadsheet className="w-8 h-8 text-success" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText className="w-8 h-8 text-destructive" />;
  }
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return <FileText className="w-8 h-8 text-info" />;
  }
  if (mimeType === 'text/plain') {
    return <FileText className="w-8 h-8 text-muted-foreground" />;
  }
  return <File className="w-8 h-8 text-muted-foreground" />;
}

function getFileIconBg(mimeType: string): string {
  if (mimeType === 'text/csv' || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return 'bg-success/15';
  }
  if (mimeType === 'application/pdf') {
    return 'bg-destructive/15';
  }
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return 'bg-info/15';
  }
  return 'bg-muted';
}

const FilePreviewChips: React.FC<FilePreviewChipsProps> = ({ files, onRemove, disabled }) => {
  if (files.length === 0) return null;

  return (
    <div className="flex gap-2 overflow-x-auto px-4 pb-2 pt-3 scrollbar-thin">
      {files.map((pf) => {
        const isImage = pf.file.type.startsWith('image/');

        return (
          <div
            key={pf.id}
            className="relative flex-shrink-0 group rounded-lg border border-border bg-muted overflow-hidden"
          >
            {isImage ? (
              <img
                src={pf.previewUrl}
                alt={pf.file.name}
                className="h-16 w-16 object-cover"
              />
            ) : (
              <div className={`h-16 w-16 flex items-center justify-center ${getFileIconBg(pf.file.type)}`}>
                {getFileIcon(pf.file.type)}
              </div>
            )}
            <div className="px-2 py-1 max-w-[120px]">
              <p className="text-xs text-foreground truncate">{pf.file.name}</p>
              <p className="text-[10px] text-muted-foreground">{formatFileSize(pf.file.size)}</p>
            </div>
            {!disabled && (
              <button
                onClick={() => onRemove(pf.id)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                aria-label={`Remove ${pf.file.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FilePreviewChips;
